import { makeAutoObservable } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { getLimitDepositRestrictionMessage } from 'src/domains/players/webview/components/ValidatorsNew/validateMinMaxAmount';
import { getBindingLimitCalculationType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimit.state';

export type ErrorType =
    | 'invalid-data'
    | 'minimum'
    | 'limit-reached'
    | 'deposits-not-allowed'
    | 'declined'
    | 'missing-fields'
    | 'failed-deposit-restriction'
    | 'limit-deposit-restriction'
    | 'kyc-deposit-restriction'
    | 'backoffice-deposit-restriction'
    | 'playbreak-deposit-restriction'
    | 'automation-deposit-restriction'
    | 'unknown';

/** Longest keys first – avoids substring false positives in debugDetails matching */
const ERROR_KEYS: ErrorType[] = [
    'backoffice-deposit-restriction',
    'automation-deposit-restriction',
    'failed-deposit-restriction',
    'limit-deposit-restriction',
    'kyc-deposit-restriction',
    'playbreak-deposit-restriction',
    'deposits-not-allowed',
    'missing-fields',
    'limit-reached',
    'invalid-data',
    'declined',
    'minimum',
];

export interface ErrorMessageType {
    type: ErrorType;
    message: string;
}

export class DepositErrorMessagesState {
    public errors: ErrorMessageType[] = [];

    private readonly configComponents: ConfigComponents;
    private readonly usersState: UsersState;
    private readonly accountHelperMail: string;

    private readonly isErrorType = (t?: string): t is ErrorType => {
        if (t === undefined) {
            return false;
        }
        if (t === 'unknown') {
            return true;
        }
        return (ERROR_KEYS as ReadonlyArray<string>).includes(t);
    };

    public constructor(
        private readonly common: Common,
        private readonly languagesState: LanguagesState
    ) {
        this.configComponents = ConfigComponents.get(this.common);
        this.usersState = UsersState.get(this.common);
        this.accountHelperMail = this.configComponents.config.accountHelperMail;

        makeAutoObservable(this);
    }

    public clearErrorMessage = (): void => {
        this.errors = [];
    };

    private getBindingLimitCalculationType = (): ReturnType<typeof getBindingLimitCalculationType> => {
        const depositLimitsResponse = this.usersState.depositLimitsData.valueReady;

        if (depositLimitsResponse === null) {
            return 'net';
        }

        if (depositLimitsResponse.responseStatus === 'apiError') {
            return 'net';
        }

        return getBindingLimitCalculationType(depositLimitsResponse.data.limits, this.configComponents.precision);
    };

    public pushErrorMessage = (type: ErrorType, debugDetails?: string, requiredFields?: Array<string>): void => {
        switch (type) {
            case 'invalid-data':
                this.errors.push({
                    type: 'invalid-data',
                    message: this.languagesState.getTranslation('errors.invalid.general', 'Some fields are invalid.'),
                });
                break;
            case 'minimum':
                this.errors.push({
                    type: 'minimum',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.deposit-minimum-amount',
                        'Minimum deposit amount is £1'
                    ),
                });
                break;
            case 'limit-reached':
                this.errors.push({
                    type: 'limit-reached',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.deposit-limit-reached',
                        "Deposit can't exceed defined limit. You can review your limits [link]."
                    ),
                });
                break;
            case 'deposits-not-allowed':
                this.errors.push({
                    type: 'deposits-not-allowed',
                    message: this.languagesState.getTranslation(
                        'errors.unauthorized-client',
                        'Your account is suspended.'
                    ),
                });
                break;
            case 'playbreak-deposit-restriction':
                this.errors.push({
                    type: 'playbreak-deposit-restriction',
                    message:
                        debugDetails !== undefined && debugDetails !== ''
                            ? this.languagesState.getTranslation(
                                  'account.top-up.errors.playbreak-deposit-restriction-with-expiry',
                                  'You cannot make a deposit because you are currently on a Play Break. Your Play Break ends on {date}.',
                                  {
                                      date: DateTime.from(debugDetails)?.format('DD-MM-YYYY HH:mm') ?? '',
                                  }
                              )
                            : this.languagesState.getTranslation(
                                  'account.top-up.errors.playbreak-deposit-restriction',
                                  'You cannot make a deposit because you are currently on a Play Break.'
                              ),
                });
                break;
            case 'limit-deposit-restriction':
                this.errors.push({
                    type: 'limit-deposit-restriction',
                    message: getLimitDepositRestrictionMessage(
                        this.languagesState,
                        this.accountHelperMail,
                        this.getBindingLimitCalculationType()
                    ),
                });
                break;
            case 'backoffice-deposit-restriction':
                this.errors.push({
                    type: 'backoffice-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.backoffice-deposit-restriction',
                        'Your deposits have been restricted. In case of any questions, please get in touch with us at {email}.',
                        {
                            email: this.accountHelperMail,
                        }
                    ),
                });
                break;
            case 'automation-deposit-restriction':
                this.errors.push({
                    type: 'automation-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.automation-deposit-restriction',
                        'Your deposits have been restricted. In case of any questions, please get in touch with us at {email}.',
                        {
                            email: this.accountHelperMail,
                        }
                    ),
                });
                break;
            case 'failed-deposit-restriction':
                if (debugDetails === undefined) {
                    return;
                }
                this.errors.push({
                    type: 'failed-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.failed-deposit-restriction',
                        "We've noticed that there were unsuccessful deposit attempts on your account due to insufficient funds. We understand that managing finances can be challenging, and we want to ensure that your gambling experience remains safe and enjoyable. To give you some time to consider your gambling spend and review your finances we have placed deposit break on your account until {date}. If you would like to discuss any of our [responsibleGamblingTools], please reach out to us at {email}. We're here to help!",
                        {
                            email: this.accountHelperMail,
                            date: DateTime.from(debugDetails)?.format('DD-MM-YYYY HH:mm') ?? '',
                        }
                    ),
                });
                break;

            case 'kyc-deposit-restriction':
                this.errors.push({
                    type: 'failed-deposit-restriction',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.kyc-deposit-restriction',
                        'Your deposits are temporarily restricted until we receive your KYC information. Please check your email for further instructions and provide the necessary details. Once verified, the restriction will be lifted. For any questions, contact us at {email}.',
                        {
                            email: this.accountHelperMail,
                        }
                    ),
                });
                break;
            case 'declined':
                this.errors.push({
                    type: 'declined',
                    message: this.languagesState.getTranslation(
                        'account.top-up.errors.declined',
                        'Your deposit has been declined, please contact your bank for further information'
                    ),
                });
                break;
            case 'missing-fields':
                if (requiredFields === undefined) {
                    return;
                }

                this.errors.push({
                    type: 'missing-fields',
                    message: `Please add customer ${requiredFields.join(', ')} to customer 'Personal account' details.`,
                });
                break;
            default:
                break;
        }
    };

    public createErrorMessage = (errorType?: string, debugDetails?: string, requiredFields?: Array<string>): void => {
        if (this.isErrorType(errorType)) {
            this.pushErrorMessage(errorType, debugDetails, requiredFields);
        }
        if (this.errors.length === 0) {
            const keyFoundInDetails: ErrorType | undefined = ERROR_KEYS.find(
                (key) => debugDetails?.toLowerCase().includes(key) === true
            );
            if (keyFoundInDetails) {
                this.pushErrorMessage(keyFoundInDetails, debugDetails, requiredFields);
                if (this.errors.length === 0) {
                    this.errors.push({ type: 'unknown', message: `Unknown error happened - ${errorType ?? ''}` });
                }
                return;
            } else {
                this.errors.push({ type: 'unknown', message: `Unknown error happened - ${errorType ?? ''}` });
            }
        }
    };
}
